import api from "./api";
import type { PanelPermission } from "./access";

// One org-owner admin governed by the global superadmin.
export interface SuperadminAdmin {
  user_id: string;
  email: string;
  name: string;
  company_name?: string | null;
  is_active: boolean;
  // True once the superadmin has set a panel matrix for this admin; otherwise
  // the admin keeps the permissive role-based fallback (sees everything).
  governed: boolean;
  // Workspace plan: "starter" (free) | "professional" | "enterprise" (custom).
  // null when the admin has no subscription row.
  plan?: string | null;
  subscription_status?: string | null;
  branchID?: string | null;
  headOfficeID?: string | null;
  created_at?: string | null;
}

// Workspace plans: starter = Free (limited workspace), professional = full
// access, enterprise = Custom (panels picked per admin).
export type WorkspacePlan = "starter" | "professional" | "enterprise";

export interface AdminSubscription {
  user_id: string;
  head_office_id: string;
  plan: WorkspacePlan | null;
  status: string | null;
  employee_limit: number | null;
  trial_end?: string | null;
  requested_plan?: string | null;
  governed: boolean;
}

export interface AdminPermissions {
  user_id: string;
  governed: boolean;
  permissions: Record<string, PanelPermission>;
}

// List every admin governed by the logged-in superadmin (cross-tenant).
export const listAdmins = async (): Promise<SuperadminAdmin[]> => {
  const res = await api.get("/superadmin/admins");
  return Array.isArray(res.data) ? res.data : [];
};

export const getAdminPermissions = async (
  userId: string
): Promise<AdminPermissions> => {
  const res = await api.get(`/superadmin/admins/${userId}/permissions`);
  return res.data;
};

export const setAdminPermissions = async (
  userId: string,
  permissions: Record<string, PanelPermission>
): Promise<AdminPermissions> => {
  const res = await api.put(`/superadmin/admins/${userId}/permissions`, {
    permissions,
  });
  return res.data;
};

// Stop governing this admin — reverts them to the permissive fallback.
export const clearAdminPermissions = async (
  userId: string
): Promise<{ user_id: string; governed: boolean }> => {
  const res = await api.delete(`/superadmin/admins/${userId}/permissions`);
  return res.data;
};

export const setAdminStatus = async (
  userId: string,
  isActive: boolean
): Promise<{ user_id: string; is_active: boolean }> => {
  const res = await api.put(`/superadmin/admins/${userId}/status`, {
    is_active: isActive,
  });
  return res.data;
};

// This admin's workspace plan/entitlement.
export const getAdminSubscription = async (
  userId: string
): Promise<AdminSubscription> => {
  const res = await api.get(`/superadmin/admins/${userId}/subscription`);
  return res.data;
};

// Move the admin's workspace onto a plan. Plan defaults (status + employee
// limit) are applied by the backend. `grantFullAccess` clears any custom panel
// matrix — used when upgrading to Professional, which means "sees everything".
// The change reaches the admin's app on their next sign-in (JWT claim).
export const setAdminPlan = async (
  userId: string,
  plan: WorkspacePlan,
  options: { grantFullAccess?: boolean; employeeLimit?: number | null } = {}
): Promise<AdminSubscription> => {
  const body: Record<string, unknown> = {
    plan,
    grant_full_access: !!options.grantFullAccess,
  };
  if (options.employeeLimit !== undefined) {
    body.employee_limit = options.employeeLimit;
  }
  const res = await api.put(`/superadmin/admins/${userId}/plan`, body);
  return res.data;
};
